<script lang="ts" setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import Splide from '@splidejs/splide';

definePageMeta({layout: "home-design"});

const page: any = await usePage('/inclusions/');

let internalHomeStyles: any = await useHomeStylesByType("INT.");
let externalHomeStyles: any = await useHomeStylesByType("EXT.");

internalHomeStyles = internalHomeStyles.value || [];
externalHomeStyles = externalHomeStyles.value || [];

const activeTab = ref<'internal' | 'external'>('internal');

const currentHomeStyles = computed(() => {
  return activeTab.value === 'internal' ? internalHomeStyles : externalHomeStyles;
});

const selectedStyleIndex = ref(0);

const selectedRooms = computed(() => {
  const styles = currentHomeStyles.value;
  if (styles && styles[selectedStyleIndex.value]?.body?.homeStylesDetails?.rooms) {
    return styles[selectedStyleIndex.value].body.homeStylesDetails.rooms;
  }
  return [];
});

const selectedStyleTitle = computed(() => {
  const styles = currentHomeStyles.value;
  if (styles && styles[selectedStyleIndex.value]?.body?.title) {
    return styles[selectedStyleIndex.value].body.title;
  }
  return 'Home Style';
});

const sections = page?.pageBuilder?.sections;

const splideRef = ref<HTMLElement | null>(null);
let splideInstance: any = null;

const splideOptions = {
  type: 'slide',
  rewind: true,
  perPage: 4.5,
  perMove: 1,
  gap: '2rem',
  padding: '3px',
  arrows: true,
  pagination: false,
  autoWidth: false,
  breakpoints: {
    768: {
      perPage: 1.5,
      gap: '1rem',
      arrows: false,
      padding: '0px'
    },
    1024: {
      perPage: 2,
      gap: '1.5rem',
    },
  },
}

const sliderLoaded = ref(false);

const initializeSplide = () => {
  if (splideRef.value) {
    if (splideInstance) {
      splideInstance.destroy();
    }

    if (currentHomeStyles.value.length >= 5) {
      splideOptions.type = 'loop';
    } else {
      splideOptions.type = 'slide';
    }

    if (currentHomeStyles.value.length < 5) {
      splideOptions.arrows = false;
    } else {
      splideOptions.arrows = true;
    }

    splideInstance = new Splide(splideRef.value, splideOptions);
    splideInstance.mount();
    
    setTimeout(() => {
      sliderLoaded.value = true;
    }, 100);

    splideInstance.on('click', (slide: any) => {
      const idx = parseInt(slide.slide.dataset.index, 10);

      const track = splideInstance.Components.Elements.track;
      const trackRect = track.getBoundingClientRect();

      const slideRect = slide.slide.getBoundingClientRect();
      const slideWidth = slideRect.width;

      // Calculate how much of the slide is visible within the track
      const visibleLeft = Math.max(slideRect.left, trackRect.left);
      const visibleRight = Math.min(slideRect.right, trackRect.right);
      const visibleWidth = visibleRight - visibleLeft;
      const visibilityRatio = visibleWidth / slideWidth;

      if (visibilityRatio < 0.9) {
        splideInstance.go('+1');
        setTimeout(() => {
          document.querySelectorAll('.splide__slide').forEach(function(el) {
            el.classList.remove('is-selected-style');
          });
          selectedStyleIndex.value = idx;
          slide.slide.classList.add('is-selected-style');
        }, 100);
      } else {
        // Slide is fully visible, just update selection
        document.querySelectorAll('.splide__slide').forEach(function(el) {
          el.classList.remove('is-selected-style');
        });
        selectedStyleIndex.value = idx;
        slide.slide.classList.add('is-selected-style');
      }
    });
  }
};

const switchTab = (tab: 'internal' | 'external') => {
  if (activeTab.value === tab) return;
  
  sliderLoaded.value = false;
  
  setTimeout(() => {
    activeTab.value = tab;
    selectedStyleIndex.value = 0;
  }, 350);
};

watch(activeTab, () => {
  nextTick(() => {
    initializeSplide();
  });
});

onMounted(() => {
  initializeSplide();
});

onUnmounted(() => {
  if (splideInstance) {
    splideInstance.destroy();
  }
});

const sliderId = 'home-style-slider';
</script>

<template>
  <div :class="`${page?.pageBuilder?.background?.showBackgroundImage ? 'bg-'+page?.pageBuilder?.background?.backgroundType[0] : ''}`">
    <HeaderCover type="page" :page="page"/>
    <div id="nav-stopper"  class="section-spacer"></div>
    
    <div class="container-xl">
      <div class="tabs-wrapper">
        <button 
          class="tab-button btn" 
          :class="{ 'active': activeTab === 'internal', 'switching': !sliderLoaded }"
          @click="switchTab('internal')"
        >
          INTERNAL
        </button>
        <button 
          class="tab-button btn" 
          :class="{ 'active': activeTab === 'external', 'switching': !sliderLoaded }"
          @click="switchTab('external')"
        >
          EXTERNAL
        </button>
      </div>
    </div>

    <div class="section-spacer-sm"></div>

    <div :class="`box-slider-wrapper home-style-slider-wrapper`">
        <div v-if="currentHomeStyles.length > 0" class="slider-container">
            <div class="container-xl">
                <div ref="splideRef" class="splide" :options="splideOptions" :id="sliderId" :key="activeTab">
                    <div class="splide__track">
                    <ul class="splide__list">
                        <li 
                          class="splide__slide" 
                          :data-index="index"
                          :class="{ 'is-selected-style': selectedStyleIndex === index }"
                          v-for="(item, index) in currentHomeStyles" 
                          :key="index"
                        >
                            <div class="slide-content">
                                <div class="slide-image">
                                    <NuxtImg v-if="item?.body?.featuredImage?.node?.mediaItemUrl" :alt="item?.body?.featuredImage?.node?.title" :src="item?.body?.featuredImage?.node?.mediaItemUrl" loading="lazy" format="webp" :quality="80" :srcset="item?.body?.featuredImage?.node?.srcSet"/>
                                </div>
                                <div class="slide-label w-100">
                                    <span class="slide-title text-dark-2">{{ item?.body?.title }}</span>
                                </div>
                            </div>
                        </li>
                    </ul>
                    </div>
                </div>
            </div>
        </div>
        <div v-else class="container-xl">
          <p class="text-center">No {{ activeTab }} styles available.</p>
        </div>
    </div>
    <div class="section-spacer-sm room-style-spacer"></div>

    <Transition name="slide-fade" mode="out-in">
      <RoomsSection
        v-if="sliderLoaded && selectedRooms && selectedRooms.length > 0"
        :key="`${activeTab}-${selectedStyleIndex}`"
        :rooms="selectedRooms"
        :title="`${selectedStyleTitle} Rooms`"
      />
    </Transition>

    <div class="section-spacer"></div>
    <template v-for="(section,index) in sections">
      <Headline v-if="section?.__typename === 'PageBuilderSectionsHeadlineLayout'" v-bind="section" :isTitleHtml="true"/>
      <BoxSlider class="box-slider"
            v-if="section?.__typename === 'PageBuilderSectionsBoxSliderLayout' && section?.slider?.length > 0" 
            :sliderId="`box-slider-${index}`" v-bind="section?.slider[0]" :addBackgroundColor="section?.addBackgroundColor"
      />
      <CollectionBlock v-if="section?.__typename === 'PageBuilderSectionsCollectionBlockLayout'" v-bind="section"/>
      <TitleBlock v-if="section?.__typename === 'PageBuilderSectionsTitleBlockLayout'" v-bind="section"/>
      <Accordion v-if="section?.__typename === 'PageBuilderSectionsAccordionLayout'" v-bind="section" />
      <ImageHeroSection v-if="section?.__typename === 'PageBuilderSectionsHeroImageSectionLayout'" v-bind="section"/>
      <ContactForm :class="`${index === (sections.length - 1) ? 'last-contact-form' : 'contact-form'}`" v-if="section?.__typename === 'PageBuilderSectionsContactFormLayout'" v-bind="section"/>
      <ImageLinkGroup v-if="section?.__typename === 'PageBuilderSectionsImageLinkGroupLayout'" v-bind="section"/>
      <div 
        v-if="section?.__typename === 'PageBuilderSectionsAccordionLayout' || 
              (index + 1 < sections.length && sections[index + 1]?.__typename === 'PageBuilderSectionsAccordionLayout')" 
        class="section-spacer bg-secondary"
      ></div>
      <div v-else class="section-spacer"></div>
      <div v-if="section?.__typename === 'PageBuilderSectionsAccordionLayout'" class="section-spacer"></div>
    </template>
  </div>
</template>

<style scoped>

.tabs-wrapper {
  display: flex;
  gap: 20px;
  border-bottom: 2px solid var(--bs-color-light);
}

.tab-button {
  border: 1px solid var(--bs-color-light);
  border-bottom: none;
  border-radius: 10px;
  border-bottom-left-radius: 0 !important;
  border-bottom-right-radius: 0 !important;
  color: #8B8B6B;
  transition: all 0.3s ease;
  text-transform: uppercase;
}

.tab-button.active {
  background: var(--bs-color-light);
  color: var(--var-dark-color);
}

.tab-button.switching.active {
  background: var(--bs-color-light);
  color: var(--var-dark-color);
  opacity: 0.7;
}

.tab-button:hover:not(.active) {
  background: rgba(139, 139, 107, 0.1);
}

.slide-fade-enter-active {
  transition: all 0.5s ease-out;
}

.slide-fade-leave-active {
  transition: all 0.3s ease-in;
}

.slide-fade-enter-from {
  opacity: 0;
  transform: translateY(20px);
}

.slide-fade-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}

.home-style-slider-wrapper .slide-content {
  padding: 12px;
  padding-bottom: 10px;
  background: var(--bs-secondary);
  border-radius: 20px;
  gap: 5px;
  height: 250px;
  cursor: pointer;
  transition: box-shadow 0.2s ease;
}

.home-style-slider-wrapper .slide-content:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.splide__slide.is-selected-style .slide-content {
  background: var(--bs-color-light);

  .slide-title {
    color: var(--bs-secondary) !important;
  }
}

.slide-label {
    display: flex;
    justify-content: start;
    text-align: left;
}

.slide-image {
  border-radius: 20px;
}

.home-style-slider-wrapper.box-slider-wrapper:not(.bg-secondary) :deep(.splide__arrow),
.home-style-slider-wrapper.box-slider-wrapper:not(.bg-secondary) :deep(.splide__arrow:hover) {
    background: #989898;
}

.home-style-slider-wrapper.box-slider-wrapper :deep(.splide__arrow--prev::after) {
    background-image: url('/left-white-arrow.svg');
    width: 30px;
    height: 30px;
}

.home-style-slider-wrapper.box-slider-wrapper :deep(.splide__arrow--next::after) {
    background-image: url('/right-white-arrow.svg');
    width: 30px;
    height: 30px;
}

.bg-bottom {
  background-position: center bottom -65px !important;
}

@media (max-width: 768px) {
  .tabs-wrapper {
    max-width: 100%;
  }

  .tab-button {
    padding: 12px 24px;
    font-size: 14px;
  }

  .section-spacer-sm.room-style-spacer {
    min-height: 30px;
  }

  .home-style-slider-wrapper, .home-style-slider-wrapper .container-xl {
    padding-right: 0 !important;
  }

  .home-style-slider-wrapper {
    padding-left: 0 !important;
  }

}
</style>